// deps
import React, { ReactElement, useCallback, useMemo } from "react";

import { useEffectOnce, ToggleSwitch } from "@swiggy-private/core-ui";

// interface
import { B2C_BALANCE_ELIGIBILITY, BillToCompanyWidgetData } from "@swiggy-private/checkout-types";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// styles
import { B2CContainer, B2CWrapper, IconContainer, ContentWrapper, Icon } from "./styles";

// js-utils
import { format, isEmpty } from "@swiggy-private/js-utils";

// constant
import { B2C_TEXTS } from "./constant";

// test-ids
import { B2C_WIDGET_TEST_IDS } from "@swiggy-private/payment-test-ids";

export interface BillToCompanyWidgetProps {
    onToggle: VoidFunction;
    data: BillToCompanyWidgetData;
    isSelected: boolean;
    onLoad?: VoidFunction;
}

function BillToCompanyWidget(props: BillToCompanyWidgetProps): ReactElement | null {
    const { onToggle, data, isSelected, onLoad } = props;
    const { title, enabled, icon, balance, disableMessage, balanceEligibility } = data;

    useEffectOnce(() => {
        onLoad && onLoad();
    });

    const onToggleClick = useCallback(() => {
        if (!data.enabled) {
            return;
        }
        onToggle();
    }, [onToggle, data]);

    const balanceText = useMemo(() => {
        if (isEmpty(balance) || balanceEligibility === B2C_BALANCE_ELIGIBILITY.UNLIMITED) {
            return "";
        }
        if (balanceEligibility === B2C_BALANCE_ELIGIBILITY.LOW_BALANCE) {
            return format(B2C_TEXTS.LOW_BALANCE_TEXT, balance);
        }
        return format(B2C_TEXTS.BALANCE_TEXT, balance);
    }, [balanceEligibility, balance]);

    const renderSubText = useCallback(() => {
        if (!isEmpty(disableMessage) && !enabled) {
            return (
                <Text
                    variant="V2.body3.neutral.regular"
                    color="text_Low_Emphasis"
                    data-testid={B2C_WIDGET_TEST_IDS.DISABLE_MESSAGE}
                >
                    {disableMessage}
                </Text>
            );
        }
        if (balanceEligibility === B2C_BALANCE_ELIGIBILITY.UNLIMITED) {
            return null;
        }
        if (!isEmpty(balance) && isSelected) {
            return (
                <Text
                    variant="V2.body3.neutral.regular"
                    color={
                        balanceEligibility === B2C_BALANCE_ELIGIBILITY.LOW_BALANCE ? "IG_Primary" : "text_Low_Emphasis"
                    }
                    data-testid={B2C_WIDGET_TEST_IDS.BALANCE_TEXT}
                >
                    {balanceText}
                </Text>
            );
        }
        return null;
    }, [disableMessage, balanceText, balanceEligibility, isSelected]);

    return (
        <B2CContainer data-testid={B2C_WIDGET_TEST_IDS.CONTAINER}>
            <B2CWrapper data-testid={B2C_WIDGET_TEST_IDS.WRAPPER}>
                {!isEmpty(icon) && (
                    <IconContainer data-testid={B2C_WIDGET_TEST_IDS.ICON_CONTAINER}>
                        <Icon cloudinaryId={icon} fit="contain" />
                    </IconContainer>
                )}
                <ContentWrapper data-testid={B2C_WIDGET_TEST_IDS.CONTENT_WRAPPER}>
                    <Text
                        variant="V2.body2.neutral.bold"
                        color={enabled ? "text_High_Emphasis" : "text_disabled"}
                        data-testid={B2C_WIDGET_TEST_IDS.TITLE}
                    >
                        {title}
                    </Text>
                    {renderSubText()}
                </ContentWrapper>
                <ToggleSwitch
                    selected={isSelected}
                    onClick={onToggleClick}
                    isDisabled={!data.enabled}
                    backgroundColor="background_Overlay_Light"
                    data-testid={B2C_WIDGET_TEST_IDS.TOGGLE_SWITCH}
                />
            </B2CWrapper>
        </B2CContainer>
    );
}

export default React.memo(BillToCompanyWidget);
